import * as React from "react";
import { Namespace, useDateFnsLocale, useT, useTypedT } from "@racoon/i18n";
import { useToast, cn } from "@racoon/ui";
import { MapWithGeofence } from "~/features/map-view/ui/map-view";
import {
  CollapsibleMapPanel,
  MAP_PANEL_CONTROL_OFFSET,
} from "~/features/map-view/components/CollapsibleMapPanel";
import { useMapPanelCollapsed } from "~/features/map-view/hooks/use-map-panel-collapsed";
import { MswRouteListSidebar } from "./msw-route-list/MswRouteListSidebar";
import { MswRouteCascade } from "./msw-route-list/MswRouteCascade";
import { AssignShiftDriverButton } from "./msw-route-list/AssignShiftDriverButton";
import { UnassignedJobsPopup } from "./msw-route-list/unassigned-jobs-popup";
import { useAddJobsToRoutePickerModal } from "../hooks/useAddJobsToRoutePickerModal";
import { useMswRouteListState } from "../hooks/msw-route-list/useMswRouteListState";
import { canAssignShiftDriver, isTtnApproved } from "~/shared/lib/shift-status";
import {
  ApprovalPill,
  ApproveButton,
  DocumentButtons,
  ShiftHeader,
  ShiftSummary,
} from "./msw-route-list/shift-header";
import { ConnectProto } from "@racoon/api";
import { useSearchParams } from "react-router-dom";
import { getEnumValue } from "@racoon/lib";
import { useApproveRouteMutation } from "~/shared/api/routes/route.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { shiftNumber } from "~/shared/lib/shift-number";
import { downloadBlob, printBlob } from "~/shared/lib/export-utils";
import { isRouteSheetJob } from "../pdf/route-sheet-pdf.data";

export type MswTransferContext = {
  selectedDate?: Date;
  routes: ConnectProto.Route[];
  selectionMap: Map<bigint, Set<bigint>>;
};

type Props = {
  onTransferContextChange?: (context: MswTransferContext) => void;
};

type MswShiftRoutesContentProps = {
  shiftId: bigint;
  selectedDate?: Date;
  routes: ConnectProto.Route[];
  isLoading: boolean;
  expandedRouteId: bigint | null;
  onExpandRoute: (shiftId: bigint, routeId: bigint | null) => void;
  onSelectionMapUpdate: (shiftId: bigint, selectionMap: Map<bigint, Set<bigint>>) => void;
  onRoutesUpdate: (shiftId: bigint, routes: ConnectProto.Route[]) => void;
  onLoadingUpdate: (shiftId: bigint, isLoading: boolean) => void;
  onRefetch: () => void;
  focusedJobId: bigint | null;
  scrollToFocusedJob: boolean;
  onJobFocus: (jobId: bigint) => void;
};
const EMPTY_ROUTES: ConnectProto.Route[] = [];

// The map column's expanded width, declared once as a CSS variable. The map sits flush
// right of the content, which takes back its width as the panel collapses.
const MSW_MAP_WIDTH_VARS = "[--msw-map-w:400px] 2xl:[--msw-map-w:500px]";
const MSW_MAP_PANEL_WIDTH = "w-[var(--msw-map-w)]";

const MswShiftRoutesContent: React.FC<MswShiftRoutesContentProps> = ({
  shiftId,
  selectedDate,
  routes,
  isLoading,
  expandedRouteId,
  onExpandRoute,
  onSelectionMapUpdate,
  onRoutesUpdate,
  onLoadingUpdate,
  onRefetch,
  focusedJobId,
  scrollToFocusedJob,
  onJobFocus,
}) => {
  const t = useTypedT(Namespace.Routes);

  React.useEffect(() => {
    onRoutesUpdate(shiftId, routes);
  }, [onRoutesUpdate, routes, shiftId]);

  React.useEffect(() => {
    onLoadingUpdate(shiftId, isLoading);
  }, [isLoading, onLoadingUpdate, shiftId]);

  const handleSelectionMapChange = React.useCallback(
    (selectionMap: Map<bigint, Set<bigint>>) => {
      onSelectionMapUpdate(shiftId, selectionMap);
    },
    [onSelectionMapUpdate, shiftId],
  );

  if (isLoading) {
    return (
      <div className="flex h-24 items-center justify-center">
        <span className="text-sm text-muted-foreground">{t("loadingRoutes")}</span>
      </div>
    );
  }
  if (routes.length === 0) {
    return (
      <div className="flex h-24 items-center justify-center">
        <span className="text-sm text-muted-foreground">{t("noRoutes")}</span>
      </div>
    );
  }

  return (
    <MswRouteCascade
      routes={routes}
      expandedRouteId={expandedRouteId}
      selectedDate={selectedDate}
      onSelectionMapChange={handleSelectionMapChange}
      onExpand={(routeId) => onExpandRoute(shiftId, routeId)}
      onRefetch={onRefetch}
      focusedJobId={focusedJobId}
      scrollToFocusedJob={scrollToFocusedJob}
      onJobFocus={onJobFocus}
    />
  );
};

export const MswRouteListPage: React.FC<Props> = ({ onTransferContextChange }) => {
  const t = useTypedT(Namespace.Routes);
  const tAny = useT();
  const [searchParams] = useSearchParams();
  const truckTypeFromUrl = searchParams.get("truck_type");
  const truckType = truckTypeFromUrl
    ? getEnumValue(ConnectProto.TruckTypeSchema, truckTypeFromUrl)
    : undefined;
  const {
    selectedDate,
    setSelectedDate,
    vehicleSearch,
    setVehicleSearch,
    sidebarMode,
    setSidebarMode,
    sidebarSections,
    selectedId,
    selectedVehicle,
    selectedVehicleShifts,
    selectedShift,
    selectedShiftRoutesApproval,
    expandedRouteByShiftId,
    focusedShiftLoading,
    activeRouteForMap,
    mapObjects,
    routeFitSeq,
    mapTrucks,
    mapDepots,
    focusedJobId,
    scrollToFocusedJob,
    focusJob,
    handleMapPinClick,
    mapFocusPoint,
    handleSidebarSelect,
    handleToggleShift,
    handleExpandRoute,
    handleRoutesUpdate,
    handleLoadingUpdate,
    allRoutesByShiftId,
    handleSelectionMapUpdate,
    refetchAllRoutes,
  } = useMswRouteListState({ onTransferContextChange, truckType });
  const { toast } = useToast();
  const { activeCompanyId, activeCompany } = useAuthSession();
  const { collapsed: mapCollapsed, toggle: toggleMapCollapsed } =
    useMapPanelCollapsed("msw-route-list");
  const dateFnsLocale = useDateFnsLocale();
  const [approveRouteMutation, { isLoading: isApproving }] = useApproveRouteMutation();
  const { openAddJobsToRoutePicker } = useAddJobsToRoutePickerModal();

  const handleApproveMsw = React.useCallback(async () => {
    const orgId = BigInt(activeCompanyId || 1);
    const activeShiftRoutes = selectedShift ? (allRoutesByShiftId.get(selectedShift.id) ?? []) : [];
    const notApprovedRoutes = activeShiftRoutes.filter(
      (route: ConnectProto.Route) => route.status === ConnectProto.RouteStatus.NOT_APPROVED,
    );
    if (notApprovedRoutes.length === 0) return;

    try {
      await Promise.all(
        notApprovedRoutes.map((route: ConnectProto.Route) =>
          approveRouteMutation({ id: route.id, ownerOrganizationId: orgId }).unwrap(),
        ),
      );
      toast({
        title: tAny(Namespace.Routes, "success"),
        description: tAny(Namespace.Routes, "routeApproved"),
      });
      refetchAllRoutes();
    } catch {
      toast({
        title: tAny(Namespace.Routes, "error"),
        description: tAny(Namespace.Routes, "routeApproveError"),
        variant: "destructive",
      });
    }
  }, [
    activeCompanyId,
    selectedShift,
    allRoutesByShiftId,
    approveRouteMutation,
    toast,
    tAny,
    refetchAllRoutes,
  ]);

  const activeShiftId = selectedShift?.id ?? null;
  // The route sheet covers the whole truck's day, not just the open tab: every
  // shift it had, each shift's routes, each route's jobs.
  const canGenerateRouteSheet = selectedVehicleShifts.some((shift) =>
    (allRoutesByShiftId.get(shift.id) ?? []).some((route) =>
      (route.jobs ?? []).some(isRouteSheetJob),
    ),
  );
  const [isGeneratingRouteSheetPdf, setIsGeneratingRouteSheetPdf] = React.useState(false);

  const handleRouteSheetAction = React.useCallback(
    async (action: "download" | "print") => {
      if (selectedVehicleShifts.length === 0 || isGeneratingRouteSheetPdf) return;

      setIsGeneratingRouteSheetPdf(true);
      try {
        const { generateRouteSheetPdfBlob, buildRouteSheetPdfFileName } = await import(
          "../pdf/route-sheet-pdf.export"
        );
        const blob = await generateRouteSheetPdfBlob({
          shifts: selectedVehicleShifts,
          routesByShiftId: allRoutesByShiftId,
          carrierName: activeCompany?.name,
          t: tAny,
          dateFnsLocale,
          date: selectedDate,
        });

        if (action === "download") {
          downloadBlob(
            blob,
            buildRouteSheetPdfFileName(selectedVehicle?.plateNumber ?? "truck", selectedDate),
          );
        } else {
          printBlob(blob);
        }
      } catch (error) {
        toast({
          title: tAny(Namespace.Routes, "error"),
          description: String((error as Error)?.message ?? error),
          variant: "destructive",
        });
      } finally {
        setIsGeneratingRouteSheetPdf(false);
      }
    },
    [
      selectedVehicleShifts,
      isGeneratingRouteSheetPdf,
      allRoutesByShiftId,
      activeCompany?.name,
      selectedVehicle?.plateNumber,
      selectedDate,
      toast,
      tAny,
      dateFnsLocale,
    ],
  );

  const shiftTabs = selectedVehicleShifts.map((shift) => ({
    id: shift.id,
    status: shift.status,
    label: t("shiftIndex", { index: shiftNumber(shift, selectedVehicleShifts) }),
  }));

  return (
    <div className="w-full h-full bg-white rounded-xl border border-ds-border-primary flex-1 overflow-hidden">
      <div className="h-full w-full grid grid-cols-[196px_minmax(0,1fr)]">
        <MswRouteListSidebar
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          searchValue={vehicleSearch}
          onSearchChange={setVehicleSearch}
          mode={sidebarMode}
          onModeChange={setSidebarMode}
          sections={sidebarSections}
          selectedId={selectedId}
          onSelect={handleSidebarSelect}
          routesByShiftId={allRoutesByShiftId}
        />

        <div className={cn("flex min-h-0 min-w-0 flex-col", MSW_MAP_WIDTH_VARS)}>
          <ShiftHeader
            tabs={shiftTabs}
            activeId={activeShiftId}
            onChange={handleToggleShift}
            center={
              <UnassignedJobsPopup
                orgId={activeCompanyId ? BigInt(activeCompanyId) : undefined}
                date={selectedDate}
                onToRoute={(jobs) =>
                  // Onto a trip of a shift, existing or new: the transfer modal offers only the
                  // trucks and shifts that can take them, and re-issues a Невывоз on the way.
                  openAddJobsToRoutePicker({
                    jobs,
                    initialDate: selectedDate,
                    onAdded: refetchAllRoutes,
                  })
                }
              />
            }
            actions={
              selectedShift && (
                <>
                  {/* Only the outstanding approval is worth a pill. */}
                  {selectedShiftRoutesApproval === "not-approved" && (
                    <ApprovalPill>{t("routesNotApproved")}</ApprovalPill>
                  )}
                  <div className="flex items-center gap-2">
                    {selectedShiftRoutesApproval === "not-approved" && (
                      <ApproveButton onClick={handleApproveMsw} disabled={isApproving}>
                        {t("approveMsw")}
                      </ApproveButton>
                    )}
                    <DocumentButtons
                      onPrint={() => handleRouteSheetAction("print")}
                      onDownload={() => handleRouteSheetAction("download")}
                      printLabel={`${t("print")} ${t("stateSheet").toLocaleLowerCase()}`}
                      downloadLabel={`${t("download")} ${t("stateSheet").toLocaleLowerCase()}`}
                      disabled={isGeneratingRouteSheetPdf}
                      reason={canGenerateRouteSheet ? undefined : t("routeSheetNoJobs")}
                    />
                  </div>
                </>
              )
            }
          />

          <div className="flex min-h-0 flex-1">
            <div className="flex min-w-0 flex-1 flex-col gap-6 p-6">
              <ShiftSummary
                shift={selectedShift}
                vehicle={selectedVehicle}
                orgName={selectedVehicle?.ownerOrganization?.name}
                date={selectedDate}
                ttnApproved={isTtnApproved(selectedShift)}
                driverFallback={t("driverNotAssigned")}
                driverSlot={
                  selectedShift && selectedVehicle && canAssignShiftDriver(selectedShift) ? (
                    <AssignShiftDriverButton shift={selectedShift} truck={selectedVehicle} />
                  ) : undefined
                }
              />

              {selectedVehicleShifts.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <span className="text-sm text-muted-foreground">{t("noRoutes")}</span>
                </div>
              ) : (
                <div className="min-h-0 min-w-0 flex-1 overflow-auto">
                  {activeShiftId !== null && (
                    <MswShiftRoutesContent
                      shiftId={activeShiftId}
                      selectedDate={selectedDate}
                      routes={allRoutesByShiftId.get(activeShiftId) ?? EMPTY_ROUTES}
                      isLoading={focusedShiftLoading}
                      expandedRouteId={expandedRouteByShiftId.get(activeShiftId) ?? null}
                      onExpandRoute={handleExpandRoute}
                      onSelectionMapUpdate={handleSelectionMapUpdate}
                      onRoutesUpdate={handleRoutesUpdate}
                      onLoadingUpdate={handleLoadingUpdate}
                      onRefetch={refetchAllRoutes}
                      focusedJobId={focusedJobId}
                      scrollToFocusedJob={scrollToFocusedJob}
                      onJobFocus={focusJob}
                    />
                  )}
                </div>
              )}
            </div>

            <CollapsibleMapPanel
              collapsed={mapCollapsed}
              onToggle={toggleMapCollapsed}
              expandedWidthClassName={MSW_MAP_PANEL_WIDTH}
              className="!rounded-none"
              frameClassName="border-l border-ds-border-primary"
            >
              <MapWithGeofence
                showPlayer
                collapsed={mapCollapsed}
                className="h-full w-full"
                controlStackClassName={MAP_PANEL_CONTROL_OFFSET}
                objects={activeRouteForMap ? mapObjects : []}
                trucks={activeRouteForMap ? mapTrucks : []}
                depots={activeRouteForMap ? mapDepots : []}
                previewRoute={activeRouteForMap ?? undefined}
                onMarkerClick={handleMapPinClick}
                focusPoint={mapFocusPoint}
                refitKey={routeFitSeq}
              />

              {focusedShiftLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white">
                  <span className="text-sm text-muted-foreground">{t("loadingMap")}</span>
                </div>
              )}
            </CollapsibleMapPanel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MswRouteListPage;
